import { Button } from "@/components/ui/button";
import { LogOut, LayoutDashboard, BookOpen, Settings } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { useMemo, useState } from "react";

const NAV_LINKS = [
  { href: "/dashboard",      label: "Dashboard",      icon: LayoutDashboard },
  { href: "/subjects",       label: "Question Banks",  icon: BookOpen },
];

export function Header() {
  const { user, isAuthenticated, logout, loading } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const path = typeof window !== "undefined" ? window.location.pathname : "";

  const initials = useMemo(() => {
    if (!user) return "U";
    if (user.name)  return user.name.slice(0, 2).toUpperCase();
    if (user.email) return user.email.slice(0, 2).toUpperCase();
    return "U";
  }, [user]);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await logout();
      window.location.href = "/";
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-[#dde3ed] shadow-sm">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-6">

        {/* Logo */}
        <a href="/" className="flex-shrink-0">
          <img
            src="/logo.svg"
            alt="Scholar Sphere"
            className="h-10 w-auto"
          />
        </a>

        {/* Centre nav — only shown when authenticated */}
        {isAuthenticated && (
          <nav className="hidden md:flex items-center gap-1 flex-1 justify-center">
            {NAV_LINKS.map(({ href, label }) => {
              const active = path === href || (href !== "/dashboard" && path.startsWith(href));
              return (
                <a
                  key={href}
                  href={href}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    active
                      ? "bg-[#1a3a6b]/10 text-[#1a3a6b]"
                      : "text-[#5a6a7e] hover:text-[#1a3a6b] hover:bg-[#1a3a6b]/5"
                  }`}
                >
                  {label}
                </a>
              );
            })}
          </nav>
        )}

        {/* Right actions */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {!isAuthenticated && !loading && (
            <>
              <Button
                variant="ghost"
                className="text-[#1a3a6b] font-semibold hover:bg-[#1a3a6b]/5"
                onClick={() => (window.location.href = "/login")}
              >
                Log in
              </Button>
              <Button
                className="bg-[#f5a623] hover:bg-[#e09310] text-[#12265a] font-bold rounded-lg shadow-none border-0"
                onClick={() => (window.location.href = "/signup")}
              >
                Start Free
              </Button>
            </>
          )}

          {isAuthenticated && (
            <Popover>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-2.5 px-3 py-1.5 rounded-xl hover:bg-[#f8f9fc] border border-transparent hover:border-[#dde3ed] transition-all">
                  <Avatar className="h-8 w-8 border-2 border-[#dde3ed]">
                    <AvatarImage src={undefined} alt={user?.name || "User"} />
                    <AvatarFallback className="bg-[#1a3a6b] text-white text-xs font-bold">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden sm:flex flex-col items-start text-left">
                    <span className="text-sm font-semibold text-[#1a2435] leading-tight">
                      {user?.name || "My Account"}
                    </span>
                    <span className="text-xs text-[#5a6a7e] leading-tight">
                      {user?.email}
                    </span>
                  </div>
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64 p-0 overflow-hidden border-[#dde3ed] shadow-lg">
                {/* User info header */}
                <div className="bg-[#1a3a6b] p-4">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10 border-2 border-white/20">
                      <AvatarFallback className="bg-[#f5a623] text-[#12265a] text-sm font-bold">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-semibold text-white leading-tight">
                        {user?.name || "Student"}
                      </p>
                      <p className="text-xs text-white/60 leading-tight">
                        {user?.email}
                      </p>
                    </div>
                  </div>
                </div>
                {/* Links */}
                <div className="p-2">
                  {[
                    { href: "/dashboard",       label: "Dashboard",      icon: LayoutDashboard },
                    { href: "/subjects",        label: "Question Banks", icon: BookOpen },
                    { href: "/settings",        label: "Settings",       icon: Settings },
                  ].map(({ href, label, icon: Icon }) => (
                    <a
                      key={href}
                      href={href}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#1a2435] font-medium hover:bg-[#f8f9fc] transition-colors"
                    >
                      <Icon className="w-4 h-4 text-[#5a6a7e]" />
                      {label}
                    </a>
                  ))}
                </div>
                <div className="border-t border-[#dde3ed] p-2">
                  <button
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                    className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-red-600 font-medium hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    {isLoggingOut ? "Logging out…" : "Log out"}
                  </button>
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>
    </header>
  );
}
